import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { Button } from "./Button";
import { Store, User } from "lucide-react";
import { useSubMenu } from "../SubMenuContext";
import { motion } from 'framer-motion';
import RoleInfo from "./RoleInfo";

export type Roles = "creator" | "lojista";

interface RoleSwitcherProps {
    onChange: (role: Roles) => void;
    value: Roles;
};


const RoleSwitcher = ({ onChange, value }: RoleSwitcherProps) => {
    const [active, setActive] = useState<Roles>(value);
    const { isSidebarOpen, setSideBarOpen } = useSubMenu();
    const buttonClass =
        "w-[107px] text-center font-inter text-sm text-gray-500 font-medium leading-5 relative z-10 flex items-center justify-center gap-1 hover:bg-transparent shrink-0 transition-colors duration-300 delay-0 px-3 box-content bg-transparent hover:bg-transparent";

    const opacityMotion = {
        initial: isSidebarOpen ? false : { opacity: 0 },
        animate: { opacity: isSidebarOpen ? 1 : 0 },
        transition: { duration: 0.1 },
        exit: { opacity: 0 }
    };

    const toggleSideBar = () => {
        if (!isSidebarOpen) {
            setSideBarOpen(true);
        }
    };

    const toggleRole = (role: Roles) => {
        toggleSideBar();
        setActive(role);
        onChange?.(role);
    }

    useEffect(() => {
        toggleRole(value);
    }, [value]);


    return (
        <>
            <div className={twMerge('relative w-full bg-gray-50 rounded-lg px-1 py-1 flex items-center gap-2 transition-all duration-100 mb-1', `${isSidebarOpen ? 'bg-gray-50' : 'bg-white'}`)}>
                <motion.div
                    initial={false}
                    animate={{
                        x: isSidebarOpen ? active === "creator" ? 0 : 139 : 0,
                        translateY: '-50%',
                    }}
                    transition={{ duration: 0.3, ease: [.52, .97, .36, 1] }}
                    className={twMerge('absolute top-1/2 -translate-y-1/2 w-[131px] h-9 bg-white pointer-events-none z-0 rounded-lg px-3 select-none whitespace-nowrap ring-offset-background focus-visible:outline-none disabled:pointer-events-none disabled:opacity-50 text-center font-inter text-xs font-medium leading-5 flex items-center justify-center gap-1  text-[#E52E7A]')}
                >
                    <div className={twMerge(`absolute w-full h-full top-0 left-0 rounded-lg`, `${isSidebarOpen ? 'shadow-[0_2px_2px_rgba(0,0,0,0.10)]' : 'shadow-none'}`)}>

                    </div>
                </motion.div>

                <Button
                    onClick={() => {
                        toggleRole("creator");
                    }}
                    className={twMerge(
                        buttonClass,
                        active === "creator" && "text-[#E52E7A]"
                    )}
                    variant={'secondary'}
                >
                    <motion.div animate={{ x: active === 'creator' ? isSidebarOpen ? 0 : -28 : 0, opacity: active === 'creator' ? 1 : isSidebarOpen ? 1 : 0 }} transition={{ duration: .2 }}>
                        <User size={16} />
                    </motion.div>
                    <motion.div {...opacityMotion} className="text-xs">
                        <span>Creator</span>
                    </motion.div>

                </Button>
                <Button
                    onClick={() => {
                        toggleRole("lojista")
                    }}
                    className={twMerge(
                        buttonClass,
                        active === "lojista" && "text-indigo-600"
                    )}
                    variant={'secondary'}
                >
                    <motion.div animate={{ x: active === 'lojista' ? isSidebarOpen ? 0 : -170 : 0, opacity: active === 'lojista' ? 1 : isSidebarOpen ? 1 : 0 }} transition={{ duration: .2 }}>
                        <Store size={16} />
                    </motion.div>
                    <motion.div {...opacityMotion} className="text-xs">
                        <span>Lojista</span>
                    </motion.div>
                </Button>
            </div>

            <RoleInfo role={active} />
        </>
    );
};

export default RoleSwitcher;
